import React from 'react';
import Identicon from 'react-identicons';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { isValidAccountHash } from '../../utils/account';
import { SvgIcon } from '@make-software/cspr-ui';
import { getImageProxyUrl } from '../../utils/cache-asset';

/* eslint-disable-next-line */
export interface AvatarProps {
  hash?: string | null;
  src?: string | null;
  path?: string | null;
  size?: 'default' | 'big' | 'medium' | 'small' | 'tiny';
  loading?: boolean;
  alt?: string;
}

const getCornerRadius = (size: AvatarProps['size'] = 'default') =>
  ({
    tiny: 2,
    small: 2,
    default: 2,
    medium: 12,
    big: 12,
  })[size];

const getSize = (size: AvatarProps['size'] = 'default') =>
  ({
    tiny: 16,
    small: 20,
    default: 32,
    medium: 80,
    big: 124,
  })[size];

const getBgColor = (size: AvatarProps['size'] = 'default') =>
  ({
    tiny: 'contentTertiary',
    small: 'contentTertiary',
    default: 'contentQuaternary',
    medium: 'contentQuaternary',
    big: 'contentQuaternary',
  })[size];

const getPadding = (size: AvatarProps['size'] = 'default') =>
  ({
    tiny: 0,
    small: 0,
    default: 0,
    medium: 0,
    big: 0,
  })[size];

const getMargin = (size: AvatarProps['size'] = 'default') =>
  ({
    tiny: 0,
    small: 0,
    default: 4,
    medium: 4,
    big: 0,
  })[size];

export const BackgroundWrapper = styled.span<{
  size: AvatarProps['size'];
  withBgColor?: boolean;
}>(({ theme, size, withBgColor = false }) => ({
  display: 'block',
  borderRadius: getCornerRadius(size),
  height: getSize(size),
  width: getSize(size),
  padding: getPadding(size),
  margin: getMargin(size),
  backgroundColor: withBgColor
    ? theme.styleguideColors[getBgColor(size)]
    : 'transparent',
  '& > canvas': {
    borderRadius: getCornerRadius(size),
  },
}));

const IconHashWrapper = styled.div(({ theme }) => ({
  color: theme.styleguideColors.contentOnFill,
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

type Ref = HTMLSpanElement;

export const Avatar = React.forwardRef<Ref, AvatarProps>(function Avatar(
  { hash, src, path, size = 'default', loading, alt, ...props }: AvatarProps,
  ref
) {
  const RETINA_SCALE = 2;
  const CACHE_TTL = '86400';

  const restProps = { ...props };

  if (loading || (!hash && !src && !path)) {
    return (
      <span ref={ref} {...restProps}>
        <Skeleton
          style={{
            borderRadius: 2,
            width: getSize(size),
            fontSize: `${getSize(size)}px`,
            margin: getMargin(size),
          }}
        />
      </span>
    );
  }

  if (src) {
    const cachedUrl = getImageProxyUrl(src, {
      ttl: CACHE_TTL,
      width: getSize(size) * RETINA_SCALE,
    });

    return (
      <span ref={ref} {...restProps}>
        <BackgroundWrapper
          size={size}
          style={{
            backgroundImage: `url("${cachedUrl}")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: 'contain',
          }}
          role={'img'}
          aria-label={alt || 'Avatar icon'}
        >
          <div style={{ width: getSize(size), height: getSize(size) }} />
        </BackgroundWrapper>
      </span>
    );
  }

  if (path) {
    return (
      <span ref={ref} {...restProps}>
        <BackgroundWrapper
          size={size}
          style={{
            backgroundImage: `url("${path}")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: 'contain',
          }}
          role={'img'}
          aria-label={alt || 'Default avatar icon'}
        >
          <div style={{ width: getSize(size), height: getSize(size) }} />
        </BackgroundWrapper>
      </span>
    );
  }

  if (hash && isValidAccountHash(hash)) {
    return (
      <span ref={ref} {...props}>
        <BackgroundWrapper size={size} withBgColor>
          <IconHashWrapper>
            <SvgIcon
              src="assets/icons/ic-hash.svg"
              size={getSize(size) - 8}
              alt={'Hash symbol'}
              role={'img'}
            />
          </IconHashWrapper>
        </BackgroundWrapper>
      </span>
    );
  }

  return (
    <span ref={ref} {...restProps}>
      <BackgroundWrapper
        size={size}
        role={'img'}
        aria-label={alt || 'Default avatar icon'}
      >
        <Identicon
          string={hash?.toLowerCase()}
          size={getSize(size) - getPadding(size)}
          bg="#fff"
          {...restProps}
        />
      </BackgroundWrapper>
    </span>
  );
});

export default Avatar;
